import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { api } from '@/lib/api'
import type { Categoria } from '@/types/database'
import { PlusIcon, EditIcon, EyeIcon, EyeOffIcon, TrashIcon, TagIcon } from '@/components/shared/Icons'

function slugify(text: string) {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

function useCategorias() {
  return useQuery({
    queryKey: ['categorias-admin'],
    queryFn: () => api.get<Categoria[]>('/api/categorias?all=true'),
  })
}

function useGuardarCategoria() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, nombre }: { id?: string; nombre: string }) => {
      const slug = slugify(nombre)
      if (id) {
        await api.put(`/api/categorias/${id}`, { nombre: nombre.trim(), slug })
      } else {
        await api.post('/api/categorias', { nombre: nombre.trim(), slug })
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['categorias-admin'] })
      qc.invalidateQueries({ queryKey: ['categorias'] })
    },
  })
}

function useToggleCategoria() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, activo }: { id: string; activo: boolean }) => {
      await api.put(`/api/categorias/${id}`, { activo })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['categorias-admin'] })
      qc.invalidateQueries({ queryKey: ['categorias'] })
    },
  })
}

function useEliminarCategoria() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.del(`/api/categorias/${id}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['categorias-admin'] })
      qc.invalidateQueries({ queryKey: ['categorias'] })
    },
  })
}

export default function CategoriasPage() {
  const { data: categorias = [], isLoading } = useCategorias()
  const guardar = useGuardarCategoria()
  const toggle = useToggleCategoria()
  const eliminar = useEliminarCategoria()

  const [modalOpen, setModalOpen] = useState(false)
  const [editando, setEditando] = useState<Categoria | null>(null)
  const [nombre, setNombre] = useState('')
  const [confirmDelete, setConfirmDelete] = useState<Categoria | null>(null)

  function abrirNueva() {
    setEditando(null)
    setNombre('')
    setModalOpen(true)
  }

  function abrirEditar(c: Categoria) {
    setEditando(c)
    setNombre(c.nombre)
    setModalOpen(true)
  }

  function cerrar() {
    setModalOpen(false)
    setEditando(null)
    setNombre('')
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!nombre.trim()) { toast.error('El nombre es obligatorio'); return }
    try {
      await guardar.mutateAsync({ id: editando?.id, nombre })
      toast.success(editando ? 'Categoria actualizada' : 'Categoria creada')
      cerrar()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Error al guardar')
    }
  }

  async function handleToggle(c: Categoria) {
    try {
      await toggle.mutateAsync({ id: c.id, activo: !c.activo })
      toast.success(c.activo ? 'Categoria desactivada' : 'Categoria activada')
    } catch {
      toast.error('Error al cambiar estado')
    }
  }

  async function handleDelete(c: Categoria) {
    try {
      await eliminar.mutateAsync(c.id)
      toast.success('Categoria eliminada')
      setConfirmDelete(null)
    } catch {
      toast.error('Error al eliminar')
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div className="me-eyebrow" style={{ marginBottom: 6 }}>Administracion</div>
          <h1 className="me-page-title">Categorias</h1>
        </div>
        <button className="me-btn me-btn-primary" onClick={abrirNueva}>
          <PlusIcon size={15} /> Nueva categoria
        </button>
      </div>

      {isLoading ? (
        <Skeleton />
      ) : categorias.length === 0 ? (
        <EmptyState onNueva={abrirNueva} />
      ) : (
        <div className="me-card" style={{ overflow: 'hidden', padding: 0 }}>
          <table className="me-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Slug</th>
                <th style={{ textAlign: 'center' }}>Estado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {categorias.map(c => (
                <tr key={c.id} style={{ opacity: c.activo ? 1 : 0.45 }}>
                  <td style={{ fontWeight: 500 }}>{c.nombre}</td>
                  <td>
                    <span style={{ fontFamily: 'var(--me-mono)', fontSize: 12, color: 'var(--me-text-3)' }}>
                      {c.slug}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span className={`me-pill ${c.activo ? 'me-pill-ok' : 'me-pill-neutral'}`}>
                      {c.activo ? 'Activa' : 'Inactiva'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
                      <ActionBtn onClick={() => abrirEditar(c)} title="Editar">
                        <EditIcon size={14} />
                      </ActionBtn>
                      <ActionBtn onClick={() => handleToggle(c)} title={c.activo ? 'Desactivar' : 'Activar'}>
                        {c.activo ? <EyeOffIcon size={14} /> : <EyeIcon size={14} />}
                      </ActionBtn>
                      <ActionBtn onClick={() => setConfirmDelete(c)} title="Eliminar" danger>
                        <TrashIcon size={14} />
                      </ActionBtn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal crear/editar */}
      {modalOpen && (
        <>
          <div
            className="me-overlay-in"
            onClick={cerrar}
            style={{ position: 'fixed', inset: 0, zIndex: 40, background: 'rgba(0,0,0,0.28)' }}
          />
          <div
            className="me-modal-in"
            style={{
              position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
              zIndex: 41, width: 380,
              background: 'var(--me-surface)',
              borderRadius: 16,
              boxShadow: 'var(--me-shadow-modal)',
              padding: 24,
            }}
          >
            <h2 className="me-section-title" style={{ marginBottom: 16 }}>
              {editando ? 'Editar categoria' : 'Nueva categoria'}
            </h2>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label className="me-label">Nombre *</label>
                <input
                  id="cat-nombre"
                  name="nombre"
                  className="me-input"
                  style={{ width: '100%' }}
                  type="text"
                  value={nombre}
                  onChange={e => setNombre(e.target.value)}
                  placeholder="Ej: Lacteos, Bebidas, Limpieza..."
                  autoComplete="off"
                  autoFocus
                />
                {nombre && (
                  <div style={{ fontSize: 11, color: 'var(--me-text-4)', marginTop: 4, fontFamily: 'var(--me-mono)' }}>
                    slug: {slugify(nombre)}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="me-btn me-btn-ghost" onClick={cerrar} style={{ flex: 1 }}>
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="me-btn me-btn-primary"
                  style={{ flex: 2 }}
                  disabled={guardar.isPending}
                >
                  {guardar.isPending ? 'Guardando...' : editando ? 'Guardar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* Modal confirmar eliminacion */}
      {confirmDelete && (
        <>
          <div
            className="me-overlay-in"
            onClick={() => setConfirmDelete(null)}
            style={{ position: 'fixed', inset: 0, zIndex: 40, background: 'rgba(0,0,0,0.28)' }}
          />
          <div
            className="me-modal-in"
            style={{
              position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
              zIndex: 41, width: 360,
              background: 'var(--me-surface)',
              borderRadius: 16,
              boxShadow: 'var(--me-shadow-modal)',
              padding: 24,
            }}
          >
            <h2 className="me-section-title" style={{ marginBottom: 8 }}>
              Eliminar categoria
            </h2>
            <p style={{ fontSize: 13.5, color: 'var(--me-text-2)', margin: '0 0 20px' }}>
              Vas a eliminar <strong>{confirmDelete.nombre}</strong>. Los productos con esta categoria quedaran sin categoria.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="me-btn me-btn-ghost"
                onClick={() => setConfirmDelete(null)}
                style={{ flex: 1 }}
              >
                Cancelar
              </button>
              <button
                className="me-btn me-btn-danger"
                onClick={() => handleDelete(confirmDelete)}
                disabled={eliminar.isPending}
                style={{ flex: 1 }}
              >
                {eliminar.isPending ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function ActionBtn({ children, onClick, title, danger }: {
  children: React.ReactNode
  onClick: () => void
  title: string
  danger?: boolean
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        background: 'none', border: 'none', cursor: 'pointer',
        color: danger ? 'var(--me-bad)' : 'var(--me-text-3)',
        borderRadius: 6, padding: 5,
        display: 'flex', alignItems: 'center',
        transition: 'background var(--me-dur-short), color var(--me-dur-short)',
        opacity: danger ? 0.6 : 1,
      }}
      onMouseOver={e => {
        e.currentTarget.style.background = danger ? 'var(--me-bad-soft)' : 'var(--me-surface-2)'
        e.currentTarget.style.color = danger ? 'var(--me-bad)' : 'var(--me-text)'
        e.currentTarget.style.opacity = '1'
      }}
      onMouseOut={e => {
        e.currentTarget.style.background = 'none'
        e.currentTarget.style.color = danger ? 'var(--me-bad)' : 'var(--me-text-3)'
        e.currentTarget.style.opacity = danger ? '0.6' : '1'
      }}
    >
      {children}
    </button>
  )
}

function EmptyState({ onNueva }: { onNueva: () => void }) {
  return (
    <div style={{ textAlign: 'center', padding: '64px 24px', color: 'var(--me-text-3)' }}>
      <TagIcon size={32} style={{ opacity: 0.3, marginBottom: 12 }} />
      <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--me-text-2)' }}>Sin categorias</div>
      <div style={{ fontSize: 13, marginBottom: 20 }}>
        Crea tu primera categoria para organizar los productos
      </div>
      <button className="me-btn me-btn-primary" onClick={onNueva}>
        <PlusIcon size={15} /> Nueva categoria
      </button>
    </div>
  )
}

function Skeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {[...Array(5)].map((_, i) => (
        <div key={i} style={{ height: 48, background: 'var(--me-surface-3)', borderRadius: 8, opacity: 1 - i * 0.15 }} />
      ))}
    </div>
  )
}
